import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import ExpenseAndIncomeCategoryFormModal from "src/pages/MasterData/ExpenseAndIncomeCategory/ExpenseAndIncomeCategoryFormModal";
import ExpenseAndIncomeSubCategoryFormModal from "src/pages/MasterData/ExpenseAndIncomeCategory/ExpenseAndIncomeSubCategoryFormModal";
import ExpenseAndIncomeTypeFormModal from "src/pages/MasterData/ExpenseAndIncomeCategory/ExpenseAndIncomeTypeFormModal";
import {
  useCreateExpenseAndIncomeCategory,
  useCreateExpenseAndIncomeSubCategory,
  useCreateExpenseAndIncomeType,
} from "src/pages/MasterData/ExpenseAndIncomeCategory/api/mutations";
import {
  expenseAndIncomeCategorySchema,
  ExpenseAndIncomeCategorySchemaType,
  expenseAndIncomeSubCategorySchema,
  ExpenseAndIncomeSubCategorySchemaType,
  expenseAndIncomeTypeSchema,
  ExpenseAndIncomeTypeSchemaType,
} from "src/pages/MasterData/ExpenseAndIncomeCategory/expenseAndIncomeCategorySchema";
import {
  handleSubmitCreateExpenseAndIncomeCategory,
  handleSubmitCreateExpenseAndIncomeSubCategory,
  handleSubmitCreateExpenseAndIncomeType,
} from "src/pages/MasterData/ExpenseAndIncomeCategory/hooks/useExpenseAndIncomeCategoryHandler";
import ItemStockUnitFormModal from "src/pages/MasterData/ItemStockUnit/ItemStockUnitFormModal";
import { useCreateItemStockUnit } from "src/pages/MasterData/ItemStockUnit/api/mutations";
import { handleSubmitCreateItemStockUnit } from "src/pages/MasterData/ItemStockUnit/hooks/useItemStockUnitHandler";
import { itemStockUnitFormSchema } from "src/pages/MasterData/ItemStockUnit/itemStockUnitSchema";
import PackageCategoryFormModal from "src/pages/MasterData/PackageCategory/PackageCategoryFormModal";
import PackageSubCategoryFormModal from "src/pages/MasterData/PackageCategory/PackageSubCategoryFormModal";
import {
  useCreatePackageCategory,
  useCreatePackageSubCategory,
} from "src/pages/MasterData/PackageCategory/api/mutations";
import {
  handleSubmitCreatePackageCategory,
  handleSubmitCreatePackageSubCategory,
} from "src/pages/MasterData/PackageCategory/hooks/usePackageCategoryHandler";
import {
  packageCategoryFormSchema,
  packageSubCategoryFormSchema,
} from "src/pages/MasterData/PackageCategory/packageCategorySchema";
import ProductCategoryFormModal from "src/pages/MasterData/ProductCategory/ProductCategoryFormModal";
import ProductSubCategoryFormModal from "src/pages/MasterData/ProductCategory/ProductSubCategoryFormModal";
import {
  useCreateProductCategory,
  useCreateProductSubCategory,
} from "src/pages/MasterData/ProductCategory/api/mutations";
import {
  handleSubmitCreateProductCategory,
  handleSubmitCreateProductSubCategory,
} from "src/pages/MasterData/ProductCategory/hooks/useProductCategoryHandler";
import {
  productCategoryFormSchema,
  productSubCategoryFormSchema,
} from "src/pages/MasterData/ProductCategory/productCategorySchema";
import { z } from "zod";

export enum CREATE_SELECTION_NEW_DATA_TYPE {
  PRODUCT_CATEGORY = "product-category",
  PRODUCT_SUB_CATEGORY = "product-sub-category",
  ITEM_STOCK_UNIT = "item-stock-unit",
  PACKAGE_CATEGORY = "package-category",
  PACKAGE_SUB_CATEGORY = "package-sub-category",
  EXPENSE_AND_INCOME_CATEGORY = "expense-and-income-category",
  EXPENSE_AND_INCOME_SUB_CATEGORY = "expense-and-income-sub-category",
  EXPENSE_AND_INCOME_TYPE = "expense-and-income-type",
}

export interface CreateSelectionNewDataModalProps {
  isShow: boolean;
  onClose: () => void;
  type: CREATE_SELECTION_NEW_DATA_TYPE;
  productCategoryId?: number;
  expenseAndIncomeType?: "expense" | "income";
}

const productCategoryDefaultValue: z.infer<typeof productCategoryFormSchema> = {
  name: "",
  description: "",
};
const productSubCategoryDefaultValue: z.infer<
  typeof productSubCategoryFormSchema
> = {
  name: "",
  color: "",
};
const itemStockUnitDefaultValue: z.infer<typeof itemStockUnitFormSchema> = {
  name: "",
  description: "",
};
const packageCategoryDefaultValue: z.infer<typeof packageCategoryFormSchema> = {
  name: "",
  description: "",
};
const packageSubCategoryDefaultValue: z.infer<
  typeof packageSubCategoryFormSchema
> = {
  name: "",
  color: "",
};
const expenseAndIncomeCategoryDefaultValue: z.infer<
  typeof expenseAndIncomeCategorySchema
> = {
  name: "",
  color: "",
};
const expenseAndIncomeSubCategoryDefaultValue: z.infer<
  typeof expenseAndIncomeSubCategorySchema
> = {
  name: "",
};
const expenseAndIncomeTypeDefaultValue: z.infer<
  typeof expenseAndIncomeTypeSchema
> = {
  name: "",
  type: "" as any,
  color: "",
};

const CreateSelectionNewDataModal = ({
  isShow,
  onClose,
  type,
  productCategoryId,
  expenseAndIncomeType,
}: CreateSelectionNewDataModalProps) => {
  const createProductCategoryMutation = useCreateProductCategory();
  const createProductSubCategoryMutation = useCreateProductSubCategory();
  const createItemStockUnitMutation = useCreateItemStockUnit();
  const createPackageCategoryMutation = useCreatePackageCategory();
  const createPackageSubCategoryMutation = useCreatePackageSubCategory();
  const createExpenseAndIncomeCategoryMutation =
    useCreateExpenseAndIncomeCategory();
  const createExpenseAndIncomeSubCategoryMutation =
    useCreateExpenseAndIncomeSubCategory();
  const createExpenseAndIncomeTypeMutation = useCreateExpenseAndIncomeType();

  const productCategoryForm = useForm<
    z.infer<typeof productCategoryFormSchema>
  >({
    resolver: zodResolver(productCategoryFormSchema),
    defaultValues: productCategoryDefaultValue,
  });
  const productSubCategoryForm = useForm<
    z.infer<typeof productSubCategoryFormSchema>
  >({
    resolver: zodResolver(productSubCategoryFormSchema),
    defaultValues: productSubCategoryDefaultValue,
  });
  const itemStockUnitForm = useForm<z.infer<typeof itemStockUnitFormSchema>>({
    resolver: zodResolver(itemStockUnitFormSchema),
    defaultValues: itemStockUnitDefaultValue,
  });
  const packageCategoryForm = useForm<
    z.infer<typeof packageCategoryFormSchema>
  >({
    resolver: zodResolver(packageCategoryFormSchema),
    defaultValues: packageCategoryDefaultValue,
  });
  const packageSubCategoryForm = useForm<
    z.infer<typeof packageSubCategoryFormSchema>
  >({
    resolver: zodResolver(packageSubCategoryFormSchema),
    defaultValues: packageSubCategoryDefaultValue,
  });
  const expenseAndIncomeCategoryForm =
    useForm<ExpenseAndIncomeCategorySchemaType>({
      resolver: zodResolver(expenseAndIncomeCategorySchema),
      defaultValues: expenseAndIncomeCategoryDefaultValue,
    });
  const expenseAndIncomeSubCategoryForm =
    useForm<ExpenseAndIncomeSubCategorySchemaType>({
      resolver: zodResolver(expenseAndIncomeSubCategorySchema),
      defaultValues: expenseAndIncomeSubCategoryDefaultValue,
    });
  const expenseAndIncomeTypeForm = useForm<ExpenseAndIncomeTypeSchemaType>({
    resolver: zodResolver(expenseAndIncomeTypeSchema),
    defaultValues: {
      ...expenseAndIncomeTypeDefaultValue,
      type: expenseAndIncomeType,
    },
  });

  useEffect(() => {
    if (isShow && expenseAndIncomeType) {
      expenseAndIncomeTypeForm.setValue("type", expenseAndIncomeType);
    }
  }, [expenseAndIncomeType, isShow]);

  const handleCloseModal = () => {
    onClose();

    productCategoryForm.reset(productCategoryDefaultValue);
    productSubCategoryForm.reset(productSubCategoryDefaultValue);
    itemStockUnitForm.reset(itemStockUnitDefaultValue);
    packageCategoryForm.reset(packageCategoryDefaultValue);
    packageSubCategoryForm.reset(packageSubCategoryDefaultValue);
    expenseAndIncomeCategoryForm.reset(expenseAndIncomeCategoryDefaultValue);
    expenseAndIncomeSubCategoryForm.reset(
      expenseAndIncomeSubCategoryDefaultValue
    );
    expenseAndIncomeTypeForm.reset(expenseAndIncomeTypeDefaultValue);
  };

  if (type === CREATE_SELECTION_NEW_DATA_TYPE.PRODUCT_CATEGORY) {
    return (
      <ProductCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={productCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreateProductCategory({
              form: productCategoryForm,
              mutation: createProductCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={productCategoryForm}
        type="CREATE"
      />
    );
  } else if (type === CREATE_SELECTION_NEW_DATA_TYPE.PRODUCT_SUB_CATEGORY) {
    return (
      <ProductSubCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={productSubCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreateProductSubCategory({
              productCategoryId: productCategoryId as number,
              form: productSubCategoryForm,
              mutation: createProductSubCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={productSubCategoryForm}
        type="CREATE"
      />
    );
  } else if (type === CREATE_SELECTION_NEW_DATA_TYPE.ITEM_STOCK_UNIT) {
    return (
      <ItemStockUnitFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={itemStockUnitForm.handleSubmit(
          async () =>
            await handleSubmitCreateItemStockUnit({
              form: itemStockUnitForm,
              mutation: createItemStockUnitMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={itemStockUnitForm}
        type="CREATE"
      />
    );
  } else if (type === CREATE_SELECTION_NEW_DATA_TYPE.PACKAGE_CATEGORY) {
    return (
      <PackageCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={packageCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreatePackageCategory({
              form: packageCategoryForm,
              mutation: createPackageCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={packageCategoryForm}
        type="CREATE"
      />
    );
  } else if (type === CREATE_SELECTION_NEW_DATA_TYPE.PACKAGE_SUB_CATEGORY) {
    return (
      <PackageSubCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={packageSubCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreatePackageSubCategory({
              packageCategoryId: productCategoryId as number,
              form: packageSubCategoryForm,
              mutation: createPackageSubCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={packageSubCategoryForm}
        type="CREATE"
      />
    );
  } else if (
    type === CREATE_SELECTION_NEW_DATA_TYPE.EXPENSE_AND_INCOME_CATEGORY
  ) {
    return (
      <ExpenseAndIncomeCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={expenseAndIncomeCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreateExpenseAndIncomeCategory({
              form: expenseAndIncomeCategoryForm,
              mutation: createExpenseAndIncomeCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={expenseAndIncomeCategoryForm}
        type="CREATE"
      />
    );
  } else if (
    type === CREATE_SELECTION_NEW_DATA_TYPE.EXPENSE_AND_INCOME_SUB_CATEGORY
  ) {
    return (
      <ExpenseAndIncomeSubCategoryFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={expenseAndIncomeSubCategoryForm.handleSubmit(
          async () =>
            await handleSubmitCreateExpenseAndIncomeSubCategory({
              expenseAndIncomeCategoryId: productCategoryId as number,
              form: expenseAndIncomeSubCategoryForm,
              mutation: createExpenseAndIncomeSubCategoryMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={expenseAndIncomeSubCategoryForm}
        type="CREATE"
      />
    );
  } else if (type === CREATE_SELECTION_NEW_DATA_TYPE.EXPENSE_AND_INCOME_TYPE) {
    return (
      <ExpenseAndIncomeTypeFormModal
        isShow={isShow}
        onClose={handleCloseModal}
        onSubmit={expenseAndIncomeTypeForm.handleSubmit(
          async () =>
            await handleSubmitCreateExpenseAndIncomeType({
              expenseAndIncomeSubCategoryId: productCategoryId as number,
              form: expenseAndIncomeTypeForm,
              mutation: createExpenseAndIncomeTypeMutation,
              handleClose: handleCloseModal,
            })
        )}
        form={expenseAndIncomeTypeForm}
        type="CREATE"
        disabledType
      />
    );
  }

  return <div />;
};

export default CreateSelectionNewDataModal;
